import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  BackstageIdentityApi,
  configApiRef,
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
  OAuthApi,
  oauthRequestApiRef,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import { LhRegistryClient, lhRegistryApiRef } from './components/lh-registry/api';
import {
  SqlPipelineClient,
  sqlPipelineApiRef,
} from '@internal/backstage-plugin-sql-pipeline';

/**
 * Keycloak OIDC 로그인 API.
 *
 * `googleAuthApiRef` 처럼 내장된 ref 가 없어서 직접 만든다 — Backstage 는 특정
 * IdP 용 ref 만 기본 제공하고, 일반 OIDC provider 는 앱이 자기 ref 를 선언한다.
 *
 * ref id 는 프론트 API 레지스트리 키이고 백엔드 providerId(`oidc`)와 별개다.
 * `auth.keycloak` 으로 두어 나중에 다른 OIDC provider 가 추가될 때 어느 쪽인지
 * 보이게 한다.
 */
export const keycloakAuthApiRef = createApiRef<
  OAuthApi & OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>({
  id: 'auth.keycloak',
});

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  ScmAuth.createDefaultApiFactory(),

  createApiFactory({
    api: keycloakAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        discoveryApi,
        oauthRequestApi,
        configApi,
        // 백엔드 providerId. app-config 의 `auth.providers.oidc` 와 같아야 한다.
        provider: {
          id: 'oidc',
          title: 'Keycloak',
          message: 'Keycloak 계정으로 로그인',
          icon: VpnKeyIcon,
        },
        environment: configApi.getOptionalString('auth.environment'),
        // 표준 세 개뿐이다. 그룹은 토큰이 아니라 **카탈로그**에서 온다 —
        // sign-in resolver 가 `signInWithCatalogUser` 로 가고 ownership 은 엔티티의
        // `memberOf` 관계로 계산된다. groups scope·claim mapper 가 필요 없다.
        defaultScopes: ['openid', 'profile', 'email'],
      }),
  }),
  // SQL Pipeline 셀프서비스 클라이언트. pluginId 문자열이 이 클라이언트
  // 한 곳에만 존재한다 — 이전에는 각 컴포넌트가 discoveryApi 를 직접 조합했다.
  createApiFactory({
    api: sqlPipelineApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: deps => new SqlPipelineClient(deps),
  }),
  createApiFactory({
    api: lhRegistryApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: deps => new LhRegistryClient(deps),
  }),
];
